import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Button,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Info,
  Business,
  AttachMoney,
  Assessment,
  LocalShipping,
  Notifications
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Mock data for the intelligence dashboard
const supplierIntelligence = {
  riskScore: {
    overall: 72,
    financial: 85,
    operational: 68,
    geopolitical: 71,
    environmental: 89
  },
  predictions: {
    priceIncreaseRisk: "23% likely in next 30 days",
    deliveryDelayProbability: "Low (12%)",
    qualityTrend: "Improving ↑ 3.2%"
  },
  recommendations: [
    "Consider diversifying suppliers for SKU-1234 (single source risk)",
    "Lock in pricing with Supplier-A before predicted 15% increase",
    "Supplier-B performance improved 40% - consider increasing allocation"
  ]
};

const riskData = [
  { name: 'Financial', value: 85, color: '#EF4444' },
  { name: 'Operational', value: 68, color: '#F59E0B' },
  { name: 'Geopolitical', value: 71, color: '#8B5CF6' },
  { name: 'Environmental', value: 89, color: '#10B981' }
];

const performanceTrends = [
  { month: 'Jan', delivery: 94, quality: 88, response: 92, cost: 76 },
  { month: 'Feb', delivery: 96, quality: 89, response: 94, cost: 78 },
  { month: 'Mar', delivery: 95, quality: 91, response: 93, cost: 79 },
  { month: 'Apr', delivery: 97, quality: 92, response: 95, cost: 81 },
  { month: 'May', delivery: 98, quality: 93, response: 96, cost: 83 },
  { month: 'Jun', delivery: 99, quality: 94, response: 97, cost: 85 }
];

const supplierSegments = [
  { name: 'Strategic', value: 8, color: '#10B981' },
  { name: 'Preferred', value: 15, color: '#3B82F6' },
  { name: 'Approved', value: 22, color: '#F59E0B' },
  { name: 'Probation', value: 5, color: '#EF4444' }
];

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

export const SupplierIntelligenceDashboard: React.FC = () => {
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [riskAlerts, setRiskAlerts] = useState(3);
  const [savingsOpportunities, setSavingsOpportunities] = useState(5);

  useEffect(() => {
    if (isLiveMode) {
      const interval = setInterval(() => {
        // Simulate live updates
        setRiskAlerts(prev => Math.max(1, Math.min(5, prev + (Math.random() > 0.5 ? 1 : -1))));
        setSavingsOpportunities(prev => Math.max(3, Math.min(8, prev + (Math.random() > 0.5 ? 1 : -1))));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isLiveMode]);

  const getRiskColor = (score: number) => {
    if (score >= 80) return '#EF4444';
    if (score >= 60) return '#F59E0B';
    return '#10B981';
  };

  const getRiskLabel = (score: number) => {
    if (score >= 80) return 'High Risk';
    if (score >= 60) return 'Medium Risk';
    return 'Low Risk';
  };



  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Live Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Supplier Intelligence Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isLiveMode}
                onChange={(e) => setIsLiveMode(e.target.checked)}
                color="primary"
              />
            }
            label="Live Mode"
          />
          <Chip
            icon={isLiveMode ? <TrendingUp /> : <TrendingDown />}
            label={isLiveMode ? 'Real-time Updates' : 'Static Mode'}
            color={isLiveMode ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Risk Alerts Banner */}
      <Alert 
        severity="warning" 
        icon={<Warning />}
        sx={{ mb: 3 }}
        action={
          <Button color="inherit" size="small">
            View All ({riskAlerts})
          </Button>
        }
      >
        <strong>Risk Alert:</strong> {supplierIntelligence.predictions.priceIncreaseRisk} - 
        Monitor Supplier-A pricing closely and consider early commitments.
      </Alert>

      {/* Main Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Overall Risk Score */}
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: `linear-gradient(135deg, ${getRiskColor(supplierIntelligence.riskScore.overall)}20 0%, ${getRiskColor(supplierIntelligence.riskScore.overall)}10 100%)`,
            border: `2px solid ${getRiskColor(supplierIntelligence.riskScore.overall)}`
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="div" color={getRiskColor(supplierIntelligence.riskScore.overall)}>
                {supplierIntelligence.riskScore.overall}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Overall Risk Score
              </Typography>
              <Chip 
                label={getRiskLabel(supplierIntelligence.riskScore.overall)}
                color={getRiskColor(supplierIntelligence.riskScore.overall) === '#EF4444' ? 'error' : 
                       getRiskColor(supplierIntelligence.riskScore.overall) === '#F59E0B' ? 'warning' : 'success'}
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Savings Opportunities */}
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb20 0%, #f5576c10 100%)',
            border: '2px solid #f093fb'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="div" color="secondary">
                ${(Math.random() * 50 + 25).toFixed(0)}K
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Monthly Savings
              </Typography>
              <Chip 
                icon={<AttachMoney />}
                label={`${savingsOpportunities} Opportunities`}
                color="secondary"
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Trend */}
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe20 0%, #00f2fe10 100%)',
            border: '2px solid #4facfe'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="div" color="info">
                94.2%
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                On-Time Delivery
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <TrendingUp color="success" />
                <Typography variant="body2" color="success.main">
                  +2.1% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Suppliers */}
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
            border: '2px solid #667eea'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="div" color="primary">
                50
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Active Suppliers
              </Typography>
              <Chip 
                icon={<Business />}
                label="8 Strategic Partners"
                color="primary"
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Risk Analysis and Performance Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Risk Breakdown */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Risk Breakdown by Category
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {riskData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Trends */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Trends (6 Months)
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="delivery" stroke="#10B981" strokeWidth={2} />
                    <Line type="monotone" dataKey="quality" stroke="#3B82F6" strokeWidth={2} />
                    <Line type="monotone" dataKey="response" stroke="#8B5CF6" strokeWidth={2} />
                    <Line type="monotone" dataKey="cost" stroke="#F59E0B" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Supplier Segmentation and AI Recommendations */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Supplier Segmentation */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Supplier Segmentation
              </Typography>
              <Box sx={{ height: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={supplierSegments}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {supplierSegments.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* AI Recommendations */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AI-Powered Recommendations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {supplierIntelligence.recommendations.map((rec, index) => (
                  <Box key={index} sx={{ 
                    p: 2, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    backgroundColor: '#fafafa'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Assessment color="primary" />
                      <Typography variant="subtitle2" color="primary">
                        Recommendation #{index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="body2" gutterBottom>
                      {rec}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button size="small" variant="outlined" color="primary">
                        Implement
                      </Button>
                      <Button size="small" variant="text" color="secondary">
                        Dismiss
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions and Alerts */}
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<LocalShipping />}
                    sx={{ mb: 1 }}
                  >
                    Create PO
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Business />}
                    sx={{ mb: 1 }}
                  >
                    Add Supplier
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Assessment />}
                    sx={{ mb: 1 }}
                  >
                    Run Analysis
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Notifications />}
                    sx={{ mb: 1 }}
                  >
                    Set Alerts
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Alerts */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Alerts & Notifications
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#fef3c7', borderRadius: 1 }}>
                  <Warning color="warning" fontSize="small" />
                  <Typography variant="body2" color="warning.dark">
                    Supplier-B delivery delayed by 2 days
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#d1fae5', borderRadius: 1 }}>
                  <CheckCircle color="success" fontSize="small" />
                  <Typography variant="body2" color="success.dark">
                    Quality score improved for Supplier-C
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#dbeafe', borderRadius: 1 }}>
                  <Info color="info" fontSize="small" />
                  <Typography variant="body2" color="info.dark">
                    New sustainability certification available
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
